/**
 * Data layer for the embedded AI tutor (Grove UI).
 *
 * All calls go to the Yudu API's /api/tutor/* proxy — the browser never sees
 * Chronicle or its service token. The turn endpoint streams NDJSON; everything
 * else is plain JSON via the shared axios client.
 */

import { api } from '@services/api';
import { supabase } from '@services/supabase';

// ── Types (mirror the API/Chronicle contracts) ───────────────────────────────

export interface TutorAssignment {
  id: string;
  source_type: 'course' | 'textbook';
  course_id: string | null;
  chronicle_textbook_id: string | null;
  framework_id: string;
  title: string;
  active: boolean;
  /**
   * When the assignment row was created (ISO). Present on the `/setup` payload
   * — the API returns the full stored row. Optional here for back-compat with
   * any caller/mock that omits it; used only as a recency tie-breaker.
   */
  created_at?: string;
}

export interface TutorSetup {
  studentId: string;
  learnerId: string;
  assignments: TutorAssignment[];
}

// ── Two-tier (chapter Topic → lesson Journey) — FROZEN API CONTRACT ──────────
//
// All fields are ADDITIVE + OPTIONAL: a flat book omits every one of them, so a
// flat payload is byte-identical to before and the flat UI is unchanged. The
// two-tier UI activates ONLY when a node carries `tier`. Hierarchy is derived
// from `parentCode` ONLY (the FE groups a flat `nodes[]` into a tree by it) —
// never by parsing codes and never from `edges` (which stay opaque).
// See AI Tutor Docs/D_RATIFICATION_ADDENDUM_2026-07-27.md § "FROZEN API CONTRACT".

/** Node depth in a two-tier framework. Absent ⇒ flat book. */
export type NodeTier = 'chapter' | 'lesson';

/** A chapter's derived rollup over its child lessons (chapter nodes only). */
export interface NodeRollup {
  childCount: number;
  masteredChildCount: number;
  /** AND-over-children state; `auto_mastered` = gate-passed roll-down. */
  childState: 'not_started' | 'in_progress' | 'complete' | 'auto_mastered';
}

/** Fields shared by MapNode + PathNode that carry two-tier structure. */
interface TwoTierFields {
  /** Parent chapter code ('ch-NNN') for a lesson; null for a chapter/flat node. */
  parentCode?: string | null;
  parentConceptId?: string | null;
  /** Present iff the framework is two-tier; the FE keys expand/collapse off it. */
  tier?: NodeTier;
}

export interface PathNode extends TwoTierFields {
  conceptId: string;
  label: string;
  standard: string;
  state: 'practicing' | 'available';
  masteryLevel: string;
  evidenceScore: number;
}

export interface LearnerPath {
  learnerId: string;
  framework: string;
  current: PathNode | null;
  upcoming: PathNode[];
  /**
   * Has the learner already COMPLETED the baseline diagnostic for this framework?
   * The truthful once-per-student marker from Chronicle (a completed diagnostic
   * run) — NOT inferred from mastery/evidence counts, which are legitimately zero
   * for a learner placed at square one. Optional for back-compat: an older API
   * that omits it reads as `undefined`, which the UI treats as "not yet complete"
   * (baseline still offered) — the safe default.
   */
  baselineComplete?: boolean;
  progress: {
    mastered: number;
    practicing: number;
    available: number;
    locked: number;
    total: number;
    /** Leaf-lesson denominator (two-tier); container chapters excluded. */
    leafTotal?: number;
    /** Leaf lessons mastered (two-tier). */
    leafMastered?: number;
  };
}

export interface MapNode extends TwoTierFields {
  conceptId: string;
  label: string;
  standard: string;
  state: 'mastered' | 'practicing' | 'available' | 'locked';
  masteryLevel: string;
  evidenceScore: number;
  /** Why a node is locked, when state === 'locked' (e.g. 'failed_check'). */
  lockedReason?: string;
  /** Chapter-node rollup over its child lessons (chapter tier only). */
  rollup?: NodeRollup | null;
}

export interface KnowledgeMap {
  learnerId: string;
  framework: string;
  nodes: MapNode[];
  edges: Array<{ from: string; to: string; type: string }>;
  frontier: Array<{ conceptId: string; label: string }>;
}

// ── Story 1: baseline diagnostic ─────────────────────────────────────────────

/** One multiple-choice check question (the correct answer is NEVER sent here). */
export interface DiagnosticQuestion {
  id: string;
  prompt: string;
  options: string[];
}

/** One diagnostic item — a concept, with real gradeable check questions. */
export interface DiagnosticItem {
  conceptId: string;
  standard: string;
  label: string;
  questions: DiagnosticQuestion[];
}

/** Per-concept progress across adaptive rounds ("narrowing in…"). */
export interface DiagnosticProgress {
  total: number;
  decided: number;
  remaining: number;
}

/**
 * One ROUND of the adaptive diagnostic (Phase 0). `start` returns round 1;
 * `answer` returns each subsequent round OR the final summary when `done`.
 * `items` is the wave to answer this round (empty when done).
 *
 * NEW (§4.4): When a cold two-tier Phase-B chapter must generate its lesson pool
 * asynchronously, `answer` may return HTTP 202 with `status:'generating'` and a
 * `generating` descriptor. The FE polls `getDiagnosticStatus` until the pool is
 * warm, then resumes the quiz loop. `status:'failed'` signals a hard generation
 * failure and the existing retry-on-resubmit safety net takes over.
 */
export interface DiagnosticRound {
  learnerId: string;
  framework: string;
  round: number;
  done: boolean;
  items: DiagnosticItem[];
  progress: DiagnosticProgress;
  /** Present only when `done` — the same shape submit returns. */
  summary?: DiagnosticSummary;
  /**
   * `'generating'` — Phase-B lesson pool is being built asynchronously; poll
   * `getDiagnosticStatus` until items arrive.
   * `'failed'` — background job failed; fall back to resubmit safety net.
   * Absent on every existing (sync) path.
   */
  status?: 'generating' | 'failed';
  /** Present when `status === 'generating'`. */
  generating?: { chapterCode: string; pollAfterMs: number };
  /** Present when `status === 'failed'`. Human-readable reason code. */
  reason?: string;
}

// `DiagnosticStart` is retained as an alias of a round for back-compat with any
// existing importer; a start IS round 1.
export type DiagnosticStart = DiagnosticRound;

/** A student's answer to one check question: the option index they chose. */
export interface DiagnosticAnswer {
  conceptId: string;
  questionId: string;
  choiceIndex: number;
}

export interface DiagnosticResult {
  conceptId: string;
  /** Human-readable node name — lets the summary say WHAT was skipped. */
  label?: string | null;
  correct: number;
  total: number;
  score: number;
  mastered: boolean;
  skip: boolean;
}

export interface DiagnosticSummary {
  learnerId: string;
  framework: string;
  gate: number;
  results: DiagnosticResult[];
  summary: { total: number; mastered: number; toLearn: number };
}

// ── Story 7 / Story 3 gate: concept check + unlock ───────────────────────────

/** The outcome of recording a mastery-check score. <gate locks, ≥gate clears. */
export interface CheckResult {
  conceptId: string;
  score: number;
  mastered: boolean;
  locked: boolean;
  lockChanged: boolean;
}

export interface UnlockResult {
  conceptId: string;
  cleared: boolean;
  locked: boolean;
}

export type TutorIntent = 'teach' | 'help' | 'validate';
export type TutorMode = 'companion' | 'tutorme';

export interface TurnRequest {
  intent: TutorIntent;
  mode: TutorMode;
  conceptId?: string;
  conceptLabel?: string;
  assignmentId?: string;
  confidence?: number;
  band?: string;
  /** The path node after the current one — the tutor can answer "what's next?". */
  nextConceptLabel?: string;
  transcript: Array<{ role: 'student' | 'tutor'; text: string }>;
}

export interface TurnResult {
  safety: { flagged: boolean; escalate: boolean; action: string };
  observedEffort?: { icap: string };
  ladder?: { rung: number; loopStep: string | null; l3Capped: boolean };
  sources?: Array<{ chapterCode: string; chapterTitle: string | null; seq: number }>;
  teach?: string[];
  hint?: string;
  check?: string;
  [k: string]: unknown;
}

export type TurnEvent =
  | { type: 'delta'; text: string }
  | { type: 'redact'; message: string }
  | { type: 'done'; result: TurnResult }
  | { type: 'error'; message: string };

// ── JSON calls ───────────────────────────────────────────────────────────────

export const fetchSetup = async (): Promise<TutorSetup> =>
  (await api.get('/api/tutor/setup')).data as TutorSetup;

export const fetchPath = async (framework: string): Promise<LearnerPath> =>
  (await api.get('/api/tutor/path', { params: { framework } })).data as LearnerPath;

export const fetchMap = async (framework: string): Promise<KnowledgeMap> =>
  (await api.get('/api/tutor/map', { params: { framework } })).data as KnowledgeMap;

// ── Story 1: baseline diagnostic ─────────────────────────────────────────────

// The FIRST diagnostic for a framework is generated by an LLM (Chronicle caches
// it per-framework afterward, so every later start is sub-second). That first
// uncached generation can take ~50s for a large framework — well over the shared
// axios 30s default (services/api.ts). Give THIS call its own 90s budget so the
// browser doesn't abort the first learner's baseline before questions arrive.
// (The Yudu API is a single Vercel function at maxDuration 800s, so the upstream
// has ample headroom; only the client cap needed lifting.)
const DIAGNOSTIC_START_TIMEOUT_MS = 90000;

// Submit grades every concept server-side and writes the mastery evidence +
// placements. Chronicle now batches those into a small constant number of DB
// round-trips (~2-3s), but a big framework on a cold serverless start can still
// exceed the shared axios 30s default (services/api.ts) — and if the browser
// aborts mid-submit, the retry story is fragile. Give submit its own 90s budget
// (mirrors start) so the client never clips a submit that the API is still
// completing. The Yudu API is a single Vercel function at maxDuration 800s, so
// the upstream has ample headroom; only the client cap needs lifting.
const DIAGNOSTIC_SUBMIT_TIMEOUT_MS = 90000;

/**
 * Open an adaptive diagnostic and fetch ROUND 1 (the first wave). The FIRST
 * diagnostic for a framework generates the question pool via an LLM (Chronicle
 * caches it per-framework afterward, so later starts are sub-second) — hence the
 * 90s budget on this call only.
 */
export const startDiagnostic = async (frameworkId: string): Promise<DiagnosticRound> =>
  (await api.post(
    '/api/tutor/diagnostic/start',
    { frameworkId },
    { timeout: DIAGNOSTIC_START_TIMEOUT_MS },
  )).data as DiagnosticRound;

/**
 * Answer ONE adaptive round. Chronicle grades the wave server-side, updates each
 * concept's Bayesian posterior, and returns EITHER the next round's wave OR the
 * final summary (`done: true`) once every concept has crossed a cut / hit the
 * item budget. Cheap after start (no LLM), but keep the generous budget so a cold
 * serverless start never clips a round.
 */
export const answerDiagnosticRound = async (
  frameworkId: string,
  answers: DiagnosticAnswer[],
): Promise<DiagnosticRound> =>
  (await api.post(
    '/api/tutor/diagnostic/answer',
    { frameworkId, answers },
    { timeout: DIAGNOSTIC_SUBMIT_TIMEOUT_MS },
  )).data as DiagnosticRound;

/**
 * Poll the async qgen status for a framework whose Phase-B lesson pool is being
 * generated in the background (§4.4). Returns one of:
 *   • `{ status:'generating', generating:{ pollAfterMs } }` — still cooking
 *   • a normal round `{ done:false, items:[…] }` — pool warm, resume quiz
 *   • `{ done:true, summary }` — sealed (all excluded / direct seal)
 *   • `{ status:'failed', reason }` — hard job failure
 *
 * Short timeout (15 s) because this is a lightweight DB read on the Chronicle
 * side — it should never be slow.
 */
export const getDiagnosticStatus = async (frameworkId: string): Promise<DiagnosticRound> =>
  (await api.get('/api/tutor/diagnostic/status', {
    params: { frameworkId },
    timeout: 15000,
  })).data as DiagnosticRound;

/**
 * BACK-COMPAT single-shot submit: grade every supplied answer at once. Chronicle
 * grades server-side and finalizes conservatively. The round-based flow
 * (`answerDiagnosticRound`) is preferred for the adaptive experience.
 */
export const submitDiagnostic = async (
  frameworkId: string,
  answers: DiagnosticAnswer[],
): Promise<DiagnosticSummary> =>
  (await api.post(
    '/api/tutor/diagnostic/submit',
    { frameworkId, answers },
    { timeout: DIAGNOSTIC_SUBMIT_TIMEOUT_MS },
  )).data as DiagnosticSummary;

// ── §8.4 Family gates: diagnostic budget / item report / placement overrides ─
//
// EVERY function here treats a 404 `not_enabled` as "feature off → return null"
// so the calling UI hides its control gracefully when the gate flag is off for
// this tenant. Any OTHER error rethrows (a real failure must not read as "off").

/** True when an axios error is the gated-off 404 (`{error:{code:'not_enabled'}}`). */
const isNotEnabled = (err: unknown): boolean => {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } | string } } };
  if (e?.response?.status !== 404) return false;
  const data = e.response.data;
  const code = typeof data?.error === 'object' ? data.error?.code : undefined;
  // Accept the canonical `{error:{code:'not_enabled'}}`; a bare 404 on these
  // endpoints also means the feature isn't wired, so treat any 404 as "off".
  return code === 'not_enabled' || code === undefined;
};

/** Estimated scope of the baseline diagnostic (concepts + item/minute ranges). */
export interface DiagnosticBudget {
  phase: 'A' | 'flat';
  concepts: number;
  estItemsLow: number;
  estItemsHigh: number;
  estMinutesLow: number;
  estMinutesHigh: number;
}

/** A parent-facing placement narrative + the skipped / to-learn concept lists. */
export interface PlacementReport {
  narrative: string;
  skipped: Array<{ conceptId: string; label: string }>;
  toLearn: Array<{ conceptId: string; label: string }>;
  generatedAt: string;
}

/** One reason a learner/staff can give when reporting a diagnostic question. */
export type ItemReportReason = 'wrong_answer' | 'confusing' | 'off_topic' | 'other';

/**
 * Fetch the baseline budget for a framework. `studentId` scopes a staff caller
 * to a student; omit it for the student's own view. Returns null when the
 * feature is off (404 not_enabled).
 */
export const getDiagnosticBudget = async (
  frameworkId: string,
  studentId?: string,
): Promise<DiagnosticBudget | null> => {
  try {
    return (
      await api.get('/api/tutor/diagnostic/budget', {
        params: { frameworkId, ...(studentId ? { studentId } : {}) },
      })
    ).data as DiagnosticBudget;
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

/**
 * Report a bad/confusing diagnostic (or check-quiz) question. Self or staff.
 * Returns null when the feature is off (404 not_enabled) so the caller can hide
 * the control; on success returns `{ ok, reportId }`.
 */
export const reportDiagnosticItem = async (input: {
  questionId: string;
  conceptId?: string;
  runId?: string;
  reason?: ItemReportReason;
  studentId?: string;
}): Promise<{ ok: boolean; reportId?: string } | null> => {
  try {
    return (await api.post('/api/tutor/diagnostic/report', input)).data as { ok: boolean; reportId?: string };
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

/**
 * STAFF-ONLY: attest that a student already knows a concept so the diagnostic
 * skips it. Returns null when the feature is off (404 not_enabled). The API
 * rejects a student caller with 403 by construction.
 */
export const markConceptKnown = async (input: {
  studentId: string;
  frameworkId: string;
  conceptId: string;
  note?: string;
}): Promise<{ ok: boolean; level?: string; score?: number } | null> => {
  try {
    return (await api.post('/api/tutor/placement/mark-known', input)).data as {
      ok: boolean;
      level?: string;
      score?: number;
    };
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

/**
 * STAFF-ONLY: clear/demote a concept's mastery so the diagnostic re-includes it.
 * Returns null when the feature is off (404 not_enabled).
 */
export const testConceptAnyway = async (input: {
  studentId: string;
  frameworkId: string;
  conceptId: string;
  note?: string;
}): Promise<{ ok: boolean } | null> => {
  try {
    return (await api.post('/api/tutor/placement/test-anyway', input)).data as { ok: boolean };
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

/**
 * Fetch the plain-language placement narrative from the learner's latest
 * completed diagnostic. `studentId` scopes a staff caller. Returns null when the
 * feature is off (404 not_enabled) OR there is no completed run yet.
 */
export const getPlacementReport = async (
  frameworkId: string,
  studentId?: string,
): Promise<PlacementReport | null> => {
  try {
    return (
      await api.get('/api/tutor/placement/report', {
        params: { frameworkId, ...(studentId ? { studentId } : {}) },
      })
    ).data as PlacementReport;
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

// ── Slice 3 (REVIEW_QUEUE_V1): spaced-retrieval review queue + coverage ───────
//
// Both reads follow the §8.4 rule: a 404 `not_enabled` (feature off for this
// tenant) → return null so the calling UI hides its card/tile; any OTHER error
// rethrows (a real failure must never read as "off").

/** One concept due (or upcoming) for spaced-retrieval review. */
export interface ReviewItem {
  conceptId: string;
  label: string;
  /** ISO due date computed from the SM-2-lite interval schedule. */
  dueAt: string;
  /** The interval (days) that produced dueAt. */
  intervalDays: number;
  /** Last time the concept was reinforced (ISO), or null if never. */
  lastReinforcedAt: string | null;
}

/** The learner's spaced-retrieval queue: what's due now + what's coming up. */
export interface ReviewQueue {
  due: ReviewItem[];
  upcoming: ReviewItem[];
  generatedAt: string;
}

/** One standard's coverage line (how many of its concepts are covered). */
export interface CoverageStandard {
  standard: string;
  label: string;
  covered: boolean;
}

/** Framework coverage rollup — covered/total concepts + a percent. */
export interface Coverage {
  framework: string;
  totalConcepts: number;
  coveredConcepts: number;
  percent: number;
  byStandard: CoverageStandard[];
}

/**
 * Fetch the spaced-retrieval review queue. `studentId` scopes a staff caller;
 * omit it for the student's own view. `frameworkId` scopes to one book/course
 * (omit for the whole picture). Returns null when the feature is off (404).
 */
export const getReviewQueue = async (
  studentId?: string,
  frameworkId?: string,
): Promise<ReviewQueue | null> => {
  try {
    return (
      await api.get('/api/tutor/review-queue', {
        params: { ...(studentId ? { studentId } : {}), ...(frameworkId ? { frameworkId } : {}) },
      })
    ).data as ReviewQueue;
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

/**
 * Fetch the framework coverage rollup. `studentId` scopes a staff caller; omit
 * it for the student's own view. `frameworkId` scopes to one book/course.
 * Returns null when the feature is off (404 not_enabled).
 */
export const getCoverage = async (
  studentId?: string,
  frameworkId?: string,
): Promise<Coverage | null> => {
  try {
    return (
      await api.get('/api/tutor/coverage', {
        params: { ...(studentId ? { studentId } : {}), ...(frameworkId ? { frameworkId } : {}) },
      })
    ).data as Coverage;
  } catch (err) {
    if (isNotEnabled(err)) return null;
    throw err;
  }
};

// ── Story 7 / Story 3 gate: concept check + unlock ───────────────────────────

/** Record a mastery-check score for one concept (< gate locks, ≥ gate clears). */
export const checkConcept = async (conceptId: string, score: number): Promise<CheckResult> =>
  (await api.post('/api/tutor/check', { conceptId, score })).data as CheckResult;

/**
 * Staff-only override that clears a failed-check lock for a student. The API
 * rejects a student caller with 403 by construction — the student surface never
 * calls this; it is exposed for the PT/teacher tutor-controls surface.
 */
export const unlockConcept = async (conceptId: string, studentId?: string): Promise<UnlockResult> =>
  (await api.post('/api/tutor/unlock', { conceptId, ...(studentId ? { studentId } : {}) })).data as UnlockResult;

// ── Streaming turn (NDJSON over fetch — axios can't stream) ─────────────────

/**
 * A turn was blocked because the concept is locked (Chronicle returns HTTP 423
 * with `concept_locked`). Thrown by `streamTurn` so the caller can surface the
 * "master this first" remediation state instead of the generic error.
 */
export class TutorLockedError extends Error {
  readonly status = 423 as const;
  readonly code: string;
  readonly conceptId?: string;
  constructor(code: string, conceptId?: string) {
    super('concept_locked');
    this.name = 'TutorLockedError';
    this.code = code;
    this.conceptId = conceptId;
  }
}

const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3100';

/**
 * Stream one tutor turn. Calls `onEvent` per NDJSON frame as it arrives.
 * Returns the final TurnResult (from the `done` frame), or null when the
 * stream ends without one. `signal` supports barge-in/unmount cancellation.
 */
export async function streamTurn(
  body: TurnRequest,
  onEvent: (event: TurnEvent) => void,
  signal?: AbortSignal,
): Promise<TurnResult | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${apiBaseUrl}/api/tutor/turn`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  // A locked concept (Story 7 / Story 3 gate) comes back as 423 — surface it as
  // a distinct locked outcome, not the generic "lost my thread" error, so the UI
  // can offer remediation. The body is JSON here (not the NDJSON stream).
  if (res.status === 423) {
    let code = 'concept_locked';
    let conceptId: string | undefined;
    try {
      const parsed = (await res.json()) as { error?: { code?: string; conceptId?: string } };
      code = parsed.error?.code ?? code;
      conceptId = parsed.error?.conceptId;
    } catch {
      /* non-JSON 423 — fall back to the defaults */
    }
    throw new TutorLockedError(code, conceptId);
  }
  if (!res.ok || !res.body) {
    const err = new Error(`tutor turn failed: ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: TurnResult | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line.length > 0) {
        try {
          const event = JSON.parse(line) as TurnEvent;
          if (event.type === 'done') result = event.result;
          onEvent(event);
        } catch {
          /* skip malformed frame */
        }
      }
      newline = buffer.indexOf('\n');
    }
  }
  return result;
}

// ── TTS ──────────────────────────────────────────────────────────────────────

/** Fetch synthesized speech for a text chunk. Returns the MP3 bytes. */
export async function fetchTts(text: string, voice?: string, rate?: string): Promise<ArrayBuffer> {
  const res = await api.post(
    '/api/tutor/tts',
    { text, voice, rate },
    { responseType: 'arraybuffer', timeout: 20000 },
  );
  return res.data as ArrayBuffer;
}

// ── Voice preference (AI Tutor Settings, student profile) ───────────────────

export interface VoiceOption {
  id: string;
  label: string;
  hint: string;
}

export const fetchVoiceOptions = async (): Promise<{ options: VoiceOption[]; default: string }> =>
  (await api.get('/api/tutor/voice-options')).data as { options: VoiceOption[]; default: string };

export const fetchVoice = async (): Promise<string> =>
  ((await api.get('/api/tutor/voice')).data as { voice: string }).voice;

export const saveVoice = async (voice: string): Promise<string> =>
  ((await api.patch('/api/tutor/voice', { voice })).data as { voice: string }).voice;

// ── Story 7 student-facing: per-concept mastery-check quiz ───────────────────

export interface CheckQuizItem {
  conceptId: string;
  label: string;
  questions: Array<{ id: string; prompt: string; options: string[] }>;
}

/** Per-item rationale returned by the backend on a failed check (F-3-FE). */
export interface FeedbackItem {
  itemId: string;
  conceptId: string;
  correct: boolean;
  rationale?: string;
}

/** Re-teach hint returned by the backend on a failed check (F-3-FE). */
export interface ReteachHint {
  conceptId: string;
  missedSummary: string;
}

export interface CheckQuizOutcome extends CheckResult {
  correct: number;
  total: number;
  /** Per-item feedback (F-3-FE). Absent = today's behavior. */
  feedback?: FeedbackItem[];
  /** Re-teach directive (F-3-FE). Absent = today's behavior. */
  reteach?: ReteachHint;
}

/** Start (or restart) the current node's mastery check — fresh server-keyed questions. */
export const startCheckQuiz = async (conceptId: string, frameworkId: string): Promise<CheckQuizItem> =>
  ((await api.post('/api/tutor/check-quiz/start', { conceptId, frameworkId })).data as { item: CheckQuizItem }).item;

/** Submit answers; the server grades against its stored key and drives the 0.90 gate. */
export const submitCheckQuiz = async (
  conceptId: string,
  answers: Array<{ questionId: string; choiceIndex: number }>,
): Promise<CheckQuizOutcome> =>
  (await api.post('/api/tutor/check-quiz/submit', { conceptId, answers })).data as CheckQuizOutcome;

// ── Real-time PT alert signals ("I'm stuck" click / idle detection) ─────────

/** Fires an immediate PT alert. Fire-and-forget by convention at call sites.
 *  F-7-FE: optionally includes the current concept so the PT sees what the
 *  student is stuck on. Absent fields = backend ignores them (back-compat). */
export const sendStuckAlert = async (conceptId?: string, conceptLabel?: string): Promise<void> => {
  await api.post('/api/tutor/stuck', {
    ...(conceptId ? { conceptId } : {}),
    ...(conceptLabel ? { conceptLabel } : {}),
  });
};

/** Fires a PT alert after sustained idle time. Fire-and-forget by convention. */
export const sendIdleAlert = async (idleMs: number): Promise<void> => {
  await api.post('/api/tutor/idle', { idleMs });
};
